import * as Sentry from '@sentry/browser'

const EXPECTED_REJECTIONS = [403, 404, 409, 422]
const QUICK_VIEW_COMPONENT = 'tasks.task-quick-view'
const TASK_PURGE_RESOURCE = 'App\\Resources\\TaskPurgeAction'
const TASK_LIFECYCLE_RESOURCE = 'App\\Resources\\TaskLifecycleAction'
const TASK_WORKSPACE_MOVE_RESOURCE = 'App\\Resources\\TaskWorkspaceMoveAction'
const QUICK_EDIT_ACTION_SUCCEEDED_EVENT = 'task-quick-edit-action-succeeded'
const QUICK_VIEW_TASK_PURGED_EVENT = 'task-quick-view-task-purged'
const QUICK_VIEW_SELECTOR = 'button[data-task-quick-view]'
const QUICK_VIEW_MODAL_SELECTOR = '[data-test="task-quick-view"]'
const CLOSE_POLL_INTERVAL_MS = 16
const CLOSE_TIMEOUT_MS = 800

function positiveSafeInteger(value) {
  if (typeof value !== 'string' || !/^[1-9][0-9]*$/.test(value)) return null

  const parsed = Number(value)

  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null
}

function jsonObject(value) {
  if (value && typeof value === 'object') return value
  if (typeof value !== 'string') return null

  try {
    const parsed = JSON.parse(value)

    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

function isQuickViewOpenRequest(payload) {
  const request = jsonObject(payload)

  return Array.isArray(request?.components) && request.components.some(component =>
    Array.isArray(component?.calls) && component.calls.some(call =>
      call?.method === '__dispatch' &&
      call?.params?.[0] === 'openModal' &&
      call?.params?.[1]?.component === QUICK_VIEW_COMPONENT
    )
  )
}

function notifyExpectedFailure(trigger) {
  window.dispatchEvent(new CustomEvent('notify', {
    detail: {
      type: 'danger',
      title: trigger?.dataset.taskQuickViewErrorTitle ?? '',
      message: trigger?.dataset.taskQuickViewErrorMessage ?? ''
    }
  }))
}

function captureUnexpectedFailure(status, content) {
  const numericStatus = Number(status)
  if (!Number.isFinite(numericStatus) || numericStatus < 500) return
  if (numericStatus === 503 && content === null) return

  const error = new Error(`taskQuickView: unexpected Livewire status ${numericStatus}`)
  Sentry.captureException(error)
  console.error(error)
}

function captureDispatchFailure(error) {
  Sentry.captureException(error)
  console.error(error)
}

function focusAfterReset(opener) {
  const target = opener?.isConnected
    ? opener
    : document.querySelector('[data-task-quick-view-focus-fallback]') ??
      document.querySelector(QUICK_VIEW_SELECTOR) ??
      document.querySelector('main button') ??
      document.querySelector('main a')

  target?.focus?.({ preventScroll: true })
}

export function taskPurgeNavigationTarget(payload, currentHref) {
  const purge = payload?.response?.taskPurge
  if (
    payload?.resourceClass !== TASK_PURGE_RESOURCE ||
    purge?.deleted !== true ||
    typeof currentHref !== 'string'
  ) return null

  const current = new URL(currentHref, window.location.origin)
  if (
    purge.boardId !== null &&
    purge.boardAvailable === false &&
    typeof purge.boardPath === 'string' &&
    current.pathname === new URL(purge.boardPath, current.origin).pathname &&
    typeof purge.fallbackPath === 'string'
  ) {
    return new URL(purge.fallbackPath, current.origin).href
  }

  return null
}

function quickViewTaskPurgeSuccess(payload) {
  if (
    payload?.response?.type !== 'success' ||
    payload?.resourceClass !== TASK_PURGE_RESOURCE ||
    payload?.response?.taskPurge?.deleted !== true ||
    !Array.isArray(payload?.modelIds) ||
    payload.modelIds.length !== 1
  ) return null

  const taskId = Number(payload.modelIds[0])
  if (!Number.isSafeInteger(taskId) || taskId < 1) return null

  return { modelIds: [taskId] }
}

function quickEditActionSuccess(payload) {
  if (
    payload?.response?.type !== 'success' ||
    ![TASK_LIFECYCLE_RESOURCE, TASK_WORKSPACE_MOVE_RESOURCE].includes(payload?.resourceClass) ||
    !Array.isArray(payload?.modelIds) ||
    payload.modelIds.length !== 1
  ) return null

  const taskId = Number(payload.modelIds[0])
  if (!Number.isSafeInteger(taskId) || taskId < 1) return null

  return {
    resourceClass: payload.resourceClass,
    modelIds: [taskId]
  }
}

/**
 * Register one delegated handler for the semantic Quickview buttons on every
 * task-card surface. Server-side authorization remains authoritative.
 */
export function initTaskQuickView(
  Livewire,
  navigate = target => window.location.assign(target)
) {
  if (document.documentElement.dataset.taskQuickViewInitialized === 'true') {
    return () => {}
  }

  document.documentElement.dataset.taskQuickViewInitialized = 'true'
  let opener = null
  let open = false
  let closing = false
  let closeTimer = null
  let requestSequence = 0
  const enhancementAvailable = typeof Livewire?.dispatch === 'function' &&
    typeof Livewire?.hook === 'function' &&
    typeof Livewire?.on === 'function'
  let stopWatchingRequests = null

  const clearCloseTimer = () => {
    if (closeTimer === null) return

    window.clearTimeout(closeTimer)
    closeTimer = null
  }

  const finishClosing = () => {
    if (!closing) return

    const previousOpener = opener
    clearCloseTimer()
    opener = null
    closing = false
    queueMicrotask(() => focusAfterReset(previousOpener))
  }

  const waitForModalRemoval = deadline => {
    if (!closing) return
    if (!document.querySelector(QUICK_VIEW_MODAL_SELECTOR) || Date.now() >= deadline) {
      finishClosing()

      return
    }

    closeTimer = window.setTimeout(
      () => waitForModalRemoval(deadline),
      CLOSE_POLL_INTERVAL_MS
    )
  }

  const releaseAfterFailure = failedOpener => {
    clearCloseTimer()
    opener = null
    open = false
    closing = false
    queueMicrotask(() => focusAfterReset(failedOpener))
  }

  if (enhancementAvailable) {
    stopWatchingRequests = Livewire.hook('request', ({ payload, fail }) => {
      if (!open || closing || typeof fail !== 'function' || !isQuickViewOpenRequest(payload)) return

      fail(({ status, content, preventDefault }) => {
        const failedOpener = opener
        const expectedFailure = EXPECTED_REJECTIONS.includes(Number(status))
        releaseAfterFailure(failedOpener)

        if (expectedFailure) {
          preventDefault?.()
          notifyExpectedFailure(failedOpener)
        }
        captureUnexpectedFailure(status, content)
      })
    })
  }

  const click = event => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey ||
      typeof event.target?.closest !== 'function'
    ) return

    const trigger = event.target.closest(QUICK_VIEW_SELECTOR)
    if (!trigger || !document.contains(trigger)) return

    const taskId = positiveSafeInteger(trigger.dataset.taskId)
    if (taskId === null || !enhancementAvailable) return

    event.preventDefault()
    if (open || closing) return

    const boardId = positiveSafeInteger(trigger.dataset.taskQuickViewBoardId)
    requestSequence = requestSequence >= Number.MAX_SAFE_INTEGER
      ? 1
      : requestSequence + 1
    const argumentsPayload = {
      requestedTaskId: taskId,
      requestedInstanceKey: requestSequence
    }
    if (boardId !== null) argumentsPayload.requestedBoardId = boardId

    opener = trigger
    open = true
    try {
      Livewire.dispatch('openModal', {
        component: QUICK_VIEW_COMPONENT,
        arguments: argumentsPayload,
        modalAttributes: { labelledBy: 'task-quick-view-title' }
      })
    } catch (error) {
      releaseAfterFailure(trigger)
      notifyExpectedFailure(trigger)
      captureDispatchFailure(error)
    }
  }

  const stopClosed = enhancementAvailable
    ? Livewire.on('modalClosed', payload => {
      if (payload?.name !== QUICK_VIEW_COMPONENT || (!open && opener === null)) return

      open = false
      closing = true
      clearCloseTimer()
      waitForModalRemoval(Date.now() + CLOSE_TIMEOUT_MS)
    })
    : null

  const stopActions = enhancementAvailable
    ? Livewire.on('action-executed', payload => {
      if (!open || closing) return

      const target = taskPurgeNavigationTarget(payload, window.location.href)
      if (target !== null) {
        clearCloseTimer()
        opener = null
        open = false
        closing = true
        navigate(target)

        return
      }

      const purgeSuccess = quickViewTaskPurgeSuccess(payload)
      if (purgeSuccess !== null) {
        Livewire.dispatch(QUICK_VIEW_TASK_PURGED_EVENT, purgeSuccess)

        return
      }

      const success = quickEditActionSuccess(payload)
      if (success !== null) {
        Livewire.dispatch(QUICK_EDIT_ACTION_SUCCEEDED_EVENT, success)
      }
    })
    : null

  document.addEventListener('click', click, false)

  return () => {
    document.removeEventListener('click', click, false)
    stopClosed?.()
    stopActions?.()
    stopWatchingRequests?.()
    clearCloseTimer()
    delete document.documentElement.dataset.taskQuickViewInitialized
    opener = null
    open = false
    closing = false
  }
}

export { positiveSafeInteger }
